<div class="bg-background min-h-screen text-on-surface pb-24">
    <!-- Hero Section -->
    <section class="pt-24 pb-12 px-6 max-w-7xl mx-auto text-center relative">
        <div class="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] pointer-events-none opacity-20">
            <div class="absolute top-[-20%] left-1/4 w-[500px] h-[500px] rounded-full bg-primary-container/40 blur-[120px]"></div>
        </div>
        
        <h1 class="text-4xl md:text-5xl font-extrabold tracking-tight text-on-background mb-6 font-['Manrope'] relative z-10">
            Simple, Transparent Pricing.
        </h1>
        
        <!-- General Features Banner -->
        <div class="flex flex-wrap justify-center gap-6 md:gap-12 mt-10 relative z-10">
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-primary font-bold">check_circle</span>
                <span class="font-bold text-sm text-on-surface-variant">14-Day Free Trial</span>
            </div>
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-primary font-bold">check_circle</span>
                <span class="font-bold text-sm text-on-surface-variant">No Credit Card Required</span>
            </div>
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-primary font-bold">check_circle</span>
                <span class="font-bold text-sm text-on-surface-variant">Unlimited Apps</span>
            </div>
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-primary font-bold">check_circle</span>
                <span class="font-bold text-sm text-on-surface-variant">Ready in 24 hours</span>
            </div>
        </div>

        <!-- Billing Toggle -->
        <div class="mt-16 flex items-center justify-center gap-4 relative z-10">
            <span class="text-sm font-bold" [class.text-on-surface-variant]="isYearly" [class.text-primary]="!isYearly">Monthly</span>
            <button (click)="toggleBilling()" class="w-16 h-8 bg-surface-container-high rounded-full relative shadow-inner flex items-center transition-all">
                <div class="w-6 h-6 bg-primary rounded-full absolute shadow-md transition-all duration-300" [ngClass]="isYearly ? 'left-9' : 'left-1'"></div>
            </button>
            <span class="text-sm font-bold" [class.text-on-surface-variant]="!isYearly" [class.text-primary]="isYearly">Yearly</span>
            <span class="bg-secondary-container text-on-secondary-container text-[10px] md:text-xs font-bold px-3 py-1 rounded-full shadow-sm animate-bounce ml-2">SAVE 16%</span>
        </div>
    </section>

    <!-- Pricing Cards -->
    <section class="max-w-7xl mx-auto px-6 relative z-10">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            
            <!-- Starter Plan -->
            <div class="bg-surface-container-lowest rounded-3xl p-8 border border-outline-variant/20 shadow-lg shadow-slate-200/40 flex flex-col hover:border-primary/30 transition-all hover:-translate-y-1">
                <h3 class="text-2xl font-extrabold text-on-surface mb-2 font-['Manrope']">Starter</h3>
                <p class="text-sm text-on-surface-variant font-medium mb-6 min-h-[40px]">Essential tools for small businesses and architectural startups.</p>
                <div class="mb-6">
                    <span class="text-4xl font-extrabold text-primary">{{ isYearly ? '1,999' : '199' }}</span>
                    <span class="text-on-surface-variant font-bold text-sm"> SAR / {{ isYearly ? 'Year' : 'Month' }}</span>
                </div>
                <a routerLink="/create-account" class="w-full text-center py-3 rounded-xl border-2 border-primary text-primary font-bold hover:bg-primary hover:text-white transition-all mb-8">Try Now</a>
                
                <div class="space-y-4 mb-8 flex-grow">
                    <p class="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Plan Details</p>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">2 Users Included</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">1 branch max</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Setup Fee: 2,500 SAR</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">24/7 Technical Support</span>
                    </div>
                    <!-- Spacers to align with Business Pro (which has Monthly Backup) -->
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                </div>

                <div class="pt-6 border-t border-outline-variant/20">
                    <p class="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-3">Add-ons Available</p>
                    <p class="text-sm font-medium text-on-surface mb-1"><span class="font-bold">Additional User:</span> {{ isYearly ? '25' : '30' }} SAR/month</p>
                    <p class="text-sm font-medium text-on-surface invisible">Placeholder</p>
                </div>
            </div>

            <!-- Growth Plan (Most Popular) -->
            <div class="bg-primary-container text-on-primary-container rounded-3xl p-8 shadow-2xl shadow-primary/20 flex flex-col transform lg:scale-105 relative z-20 border border-primary/20">
                <div class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-md">Most Popular</div>
                <h3 class="text-2xl font-extrabold mb-2 font-['Manrope']">Growth</h3>
                <p class="text-sm font-medium opacity-80 mb-6 min-h-[40px]">Perfect for expanding companies needing advanced logic and scale.</p>
                <div class="mb-6">
                    <span class="text-4xl font-extrabold">{{ isYearly ? '7,999' : '799' }}</span>
                    <span class="font-bold text-sm opacity-80"> SAR / {{ isYearly ? 'Year' : 'Month' }}</span>
                </div>
                <a routerLink="/create-account" class="w-full text-center py-3 rounded-xl bg-primary text-white font-bold hover:shadow-lg hover:opacity-90 transition-all mb-8 shadow-md">Try Now</a>
                
                <div class="space-y-4 mb-8 flex-grow">
                    <p class="text-xs font-bold uppercase tracking-wider opacity-70">Plan Details</p>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-semibold">5 Users Included</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-semibold">Up to 2 branches</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-semibold">Setup Fee: 3,500 SAR</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-semibold">Multiple UI Languages</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-semibold">Multiple Currencies</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-semibold">Multiple Companies</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-semibold">24/7 Technical Support</span>
                    </div>
                    <!-- Spacers to align with Business Pro (which has Monthly Backup) -->
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                    <div class="h-[28px]"></div>
                </div>

                <div class="pt-6 border-t border-primary/10">
                    <p class="text-xs font-bold uppercase tracking-wider opacity-70 mb-3">Add-ons Available</p>
                    <p class="text-sm font-semibold mb-1"><span class="font-extrabold">Additional User:</span> {{ isYearly ? '25' : '30' }} SAR/month</p>
                    <p class="text-sm font-semibold"><span class="font-extrabold">Additional Branch:</span> {{ isYearly ? '125' : '150' }} SAR/month</p>
                </div>
            </div>

            <!-- Business Pro Plan -->
            <div class="bg-surface-container-lowest rounded-3xl p-8 border border-outline-variant/20 shadow-lg shadow-slate-200/40 flex flex-col hover:border-primary/30 transition-all hover:-translate-y-1">
                <h3 class="text-2xl font-extrabold text-on-surface mb-2 font-['Manrope']">Business Pro</h3>
                <p class="text-sm text-on-surface-variant font-medium mb-6 min-h-[40px]">A complete suite for multi-branch regional enterprises.</p>
                <div class="mb-6">
                    <span class="text-4xl font-extrabold text-primary">{{ isYearly ? '24,999' : '2,499' }}</span>
                    <span class="text-on-surface-variant font-bold text-sm"> SAR / {{ isYearly ? 'Year' : 'Month' }}</span>
                </div>
                <a routerLink="/create-account" class="w-full text-center py-3 rounded-xl border-2 border-primary text-primary font-bold hover:bg-primary hover:text-white transition-all mb-8">Try Now</a>
                
                <div class="space-y-4 mb-8 flex-grow">
                    <p class="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Plan Details</p>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">10 Users Included</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Up to 5 branches</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Setup Fee: 7,500 SAR</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Multiple UI Languages</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Multiple Currencies</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Multiple Companies</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">24/7 Technical Support</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Monthly Backup</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Backup Control</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">API Integration Access</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">API Documentation</span>
                    </div>
                </div>

                <div class="pt-6 border-t border-outline-variant/20">
                    <p class="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-3">Add-ons Available</p>
                    <p class="text-sm font-medium text-on-surface mb-1"><span class="font-bold">Additional User:</span> {{ isYearly ? '25' : '30' }} SAR/month</p>
                    <p class="text-sm font-medium text-on-surface"><span class="font-bold">Additional Branch:</span> {{ isYearly ? '125' : '150' }} SAR/month</p>
                </div>
            </div>

            <!-- Enterprise Plan -->
            <div class="bg-surface-container-lowest rounded-3xl p-8 border border-outline-variant/20 shadow-lg shadow-slate-200/40 flex flex-col bg-gradient-to-br from-surface-container-lowest to-surface-container-low hover:border-primary/30 transition-all hover:-translate-y-1">
                <h3 class="text-2xl font-extrabold text-on-surface mb-2 font-['Manrope']">Enterprise</h3>
                <p class="text-sm text-on-surface-variant font-medium mb-6 min-h-[40px]">Custom architectural solutions for massive global organizations.</p>
                <div class="mb-6">
                    <span class="text-sm font-bold text-on-surface-variant">Starts From</span><br/>
                    <span class="text-4xl font-extrabold text-primary">{{ isYearly ? '51,000' : '5,000' }}</span>
                    <span class="text-on-surface-variant font-bold text-sm"> SAR / {{ isYearly ? 'Year' : 'Month' }}</span>
                </div>
                <a routerLink="/contact" class="w-full text-center py-3 rounded-xl bg-on-surface text-surface-container-lowest font-bold hover:opacity-90 transition-all mb-8 shadow-md">Try Now</a>
                
                <div class="space-y-4 mb-8 flex-grow">
                    <p class="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Plan Details</p>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Users Included: Custom (Upon Agreement)</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Branches: Unlimited</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Setup Fee: Depends on the project</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Multiple UI Languages</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Multiple Currencies</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Multiple Companies</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">24/7 Technical Support</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Monthly Backup</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">Backup Control</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">API Integration Access</span>
                    </div>
                    <div class="flex items-start gap-3">
                        <span class="material-symbols-outlined text-primary text-[20px]">check</span>
                        <span class="text-sm font-medium text-on-surface">API Documentation</span>
                    </div>
                </div>

                <div class="pt-6 border-t border-outline-variant/20">
                    <p class="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-3">Add-ons Available</p>
                    <p class="text-sm font-medium text-on-surface mb-1"><span class="font-bold">Additional User:</span> {{ isYearly ? '25' : '30' }} SAR/month</p>
                    <p class="text-sm font-medium text-on-surface"><span class="font-bold">Additional Branch:</span> {{ isYearly ? '125' : '150' }} SAR/month</p>
                </div>
            </div>
        </div>

        <!-- Wide Compare Button Below Plans -->
        <div class="mt-12 flex justify-center">
            <button (click)="openCompareModal()" class="w-full max-w-7xl py-5 bg-surface-container-low hover:bg-surface-container-high text-primary font-black text-lg rounded-3xl border-2 border-dashed border-primary/20 transition-all flex items-center justify-center gap-3 group">
                <span class="material-symbols-outlined text-2xl group-hover:scale-110 transition-transform">compare_arrows</span>
                Click here to compare all plan features and find your perfect fit
            </button>
        </div>
    </section>

    <!-- Compare Plans Modal -->
    <div *ngIf="isCompareModalOpen" class="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" (click)="closeCompareModal()"></div>
        
        <!-- Modal Content -->
        <div class="bg-surface-container-lowest w-full max-w-6xl max-h-[90vh] rounded-[2rem] shadow-2xl relative z-[110] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-300">
            <!-- Modal Header -->
            <div class="p-8 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low/50">
                <div>
                    <h2 class="text-2xl font-black tracking-tight text-on-surface">Detailed Plan Comparison</h2>
                    <p class="text-sm text-on-surface-variant mt-1">Deep dive into our architectural workspace features.</p>
                </div>
                <button (click)="closeCompareModal()" class="w-12 h-12 rounded-full hover:bg-surface-container-high flex items-center justify-center transition-colors">
                    <span class="material-symbols-outlined text-2xl">close</span>
                </button>
            </div>

            <!-- Modal Body (Scrollable Table) -->
            <div class="flex-grow overflow-auto p-8">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="text-center">
                            <th class="p-4 text-left text-sm font-bold uppercase tracking-widest text-on-surface-variant/60 w-1/4">Features</th>
                            <th class="p-4 text-sm font-black text-on-surface w-[18%]">Starter</th>
                            <th class="p-4 text-sm font-black text-primary w-[18%] bg-primary/5 rounded-t-2xl">Growth</th>
                            <th class="p-4 text-sm font-black text-on-surface w-[18%]">Business Pro</th>
                            <th class="p-4 text-sm font-black text-on-surface w-[18%]">Enterprise</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-outline-variant/10">
                        <ng-container *ngFor="let group of featureGroups">
                            <!-- Group Title -->
                            <tr class="bg-surface-container-low/30">
                                <td colspan="5" class="px-4 py-3 text-xs font-black uppercase tracking-widest text-primary">{{group.name}}</td>
                            </tr>
                            <!-- Feature Rows -->
                            <tr *ngFor="let feature of group.features" class="hover:bg-surface-container-low transition-colors">
                                <td class="px-4 py-5 text-sm font-semibold text-on-surface">{{feature.name}}</td>
                                <td class="px-4 py-5 text-center text-sm font-medium">
                                    <div class="inline-flex items-center justify-center gap-1.5">
                                        <span *ngIf="feature.starter === 'yes'" class="material-symbols-outlined text-primary">check_circle</span>
                                        <span *ngIf="feature.starter === 'no'" class="material-symbols-outlined text-outline-variant opacity-40">remove</span>
                                        <span *ngIf="feature.starter !== 'yes' && feature.starter !== 'no'">{{feature.starter}}</span>
                                    </div>
                                </td>
                                <td class="px-4 py-5 text-center text-sm font-bold bg-primary/5">
                                    <div class="inline-flex items-center justify-center gap-1.5">
                                        <span *ngIf="feature.growth === 'yes'" class="material-symbols-outlined text-primary">check_circle</span>
                                        <span *ngIf="feature.growth === 'no'" class="material-symbols-outlined text-outline-variant opacity-40">remove</span>
                                        <span *ngIf="feature.growth !== 'yes' && feature.growth !== 'no'">{{feature.growth}}</span>
                                    </div>
                                </td>
                                <td class="px-4 py-5 text-center text-sm font-medium">
                                    <div class="inline-flex items-center justify-center gap-1.5">
                                        <span *ngIf="feature.business === 'yes'" class="material-symbols-outlined text-primary">check_circle</span>
                                        <span *ngIf="feature.business === 'no'" class="material-symbols-outlined text-outline-variant opacity-40">remove</span>
                                        <span *ngIf="feature.business !== 'yes' && feature.business !== 'no'">{{feature.business}}</span>
                                    </div>
                                </td>
                                <td class="px-4 py-5 text-center text-sm font-medium">
                                    <div class="inline-flex items-center justify-center gap-1.5">
                                        <span *ngIf="feature.enterprise === 'yes'" class="material-symbols-outlined text-primary">check_circle</span>
                                        <span *ngIf="feature.enterprise === 'no'" class="material-symbols-outlined text-outline-variant opacity-40">remove</span>
                                        <span *ngIf="feature.enterprise !== 'yes' && feature.enterprise !== 'no'">{{feature.enterprise}}</span>
                                    </div>
                                </td>
                            </tr>
                        </ng-container>
                    </tbody>
                </table>
            </div>

            <!-- Modal Footer -->
            <div class="p-8 border-t border-outline-variant/10 bg-surface-container-low/50 flex justify-end gap-4">
                <button (click)="closeCompareModal()" class="px-6 py-2.5 font-bold text-sm text-on-surface-variant hover:text-on-surface transition-colors">Close</button>
                <button (click)="closeCompareModal()" class="bg-primary text-white px-8 py-2.5 rounded-xl font-bold text-sm shadow-md hover:shadow-primary/20 transition-all active:scale-95">Choose a Plan</button>
            </div>
        </div>
    </div>

    <!-- Call to Action Banner -->
    <section class="max-w-5xl mx-auto px-6 mt-24">
        <div class="bg-gradient-to-r from-[#026BFF] to-[#0054CD] rounded-[2rem] p-12 text-center text-white shadow-2xl relative overflow-hidden">
            <div class="absolute top-[-50%] right-[-10%] w-[300px] h-[300px] rounded-full bg-white/10 blur-[50px]"></div>
            <h2 class="text-3xl font-extrabold mb-4 font-['Manrope'] relative z-10">Start your architectural transformation today.</h2>
            <p class="text-blue-100 mb-8 max-w-2xl mx-auto font-medium relative z-10">
                Join 1,000+ businesses scaling with ZAD ERP. Set up your workspace in minutes.
            </p>
            <a routerLink="/create-account" class="inline-flex items-center gap-2 bg-white text-[#0054CD] px-8 py-4 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all relative z-10">
                Try It Now
                <span class="material-symbols-outlined text-sm">arrow_forward</span>
            </a>
        </div>
    </section>
</div>